import clsx from "clsx";
import { useEffect, useState } from "react";
import { Button, Collapse, Image } from "react-bootstrap";
import { IoChevronDownOutline } from "react-icons/io5";
import { RiLogoutBoxRLine } from "react-icons/ri";
import { Link, useLocation } from "react-router-dom";

import WebService from "../../Services/WebService";
import ProfileAvtar from "../../assets/images/default-avatar.jpg";
import { MenuKeys, useMenu } from "../../context/MenuContext";
import ConfirmationModal from "../Common/Modals/ConfirmationModal";

/* ---------------- ICONS ---------------- */
import IconDashboard from "../../assets/images/menu-icon/icon-dashboard.svg";
import IconEmpAttn from "../../assets/images/menu-icon/icon-emp-attendance.svg";
import IconEmpDir from "../../assets/images/menu-icon/icon-emp-dir.svg";
import IconInvoicereport from "../../assets/images/menu-icon/icon-invoice.svg";
import IconOutlet from "../../assets/images/menu-icon/icon-outlet.svg";
import IconPlan from "../../assets/images/menu-icon/icon-planning.svg";
import IconProfitAnalytics from "../../assets/images/menu-icon/icon-profit-analytics.svg";
import IconRecipes from "../../assets/images/menu-icon/icon-receipes.svg";
import IconSalesReport from "../../assets/images/menu-icon/icon-sales-report.svg";
import IconSKU from "../../assets/images/menu-icon/icon-sku.svg";
import IconStock from "../../assets/images/menu-icon/icon-stock.svg";
import IconSupplyChain from "../../assets/images/menu-icon/icon-suplly-chain.svg";
import IconTaxation from "../../assets/images/menu-icon/icon-taxation.svg";
import IconTransactions from "../../assets/images/menu-icon/icon-transactions.svg";
import IconWaste from "../../assets/images/menu-icon/icon-waste.svg";
// CRM
import { useSelector } from "react-redux";
import IconCampaignControl from "../../assets/images/menu-icon/icon-campaign-control.svg";
import IconCustomerDashboard from "../../assets/images/menu-icon/icon-cust-dashboard.svg";
import IconCustomerSegment from "../../assets/images/menu-icon/icon-customer.svg";
import IconFinanceAnalysis from "../../assets/images/menu-icon/icon-finance-analysis.svg";
import IconFXSupport from "../../assets/images/menu-icon/icon-fx-support.svg";
import IconNewsFeed from "../../assets/images/menu-icon/icon-news-feed.svg";
import IconIntegration from "../../assets/images/menu-icon/icon-integrations.svg";
import IconHostessView from "../../assets/images/menu-icon/icon-hostess-view.svg";
import IconKDS from "../../assets/images/menu-icon/icon-kds.svg";
import IconLoyalty from "../../assets/images/menu-icon/icon-loyalty.svg";
import IconMenuManagment from "../../assets/images/menu-icon/icon-menu-managment.svg";
import IconOffersCombo from "../../assets/images/menu-icon/icon-offers-combo.svg";
import IconSettings from "../../assets/images/menu-icon/icon-settings.svg";
import IconTableManagment from "../../assets/images/menu-icon/icon-table-management.svg";
import { MenuItem } from "../../assets/interfaces/commom_interfaces";
import AiChat from "../AIChat/AiChat";
import { useOnboarding } from "../../context/OnboardingContext";


/* ---------------- TYPES ---------------- */


/* ---------------- ICON MAP ---------------- */
const MENU_ICON_MAP: Record<string, string> = {
    // POS
    POS_DASHBOARD: IconDashboard,
    POS_MENU_MANAGEMENT: IconMenuManagment,
    POS_OFFER_COMBOS: IconOffersCombo,
    POS_TABLE_MANAGEMENT: IconTableManagment,
    POS_HOSTESS_VIEW: IconHostessView,
    POS_KDS: IconKDS,

    // INVENTORY
    INVENTORY_DASHBOARD: IconDashboard,
    INVENTORY_SUPPLY_CHAIN_MANAGEMENT: IconSupplyChain,
    INVENTORY_STOCK_REAL_TIME: IconStock,
    INVENTORY_BRANCH_TRANSFER: IconOutlet,
    INVENTORY_WASTE_CANCELLATION_MANAGEMENT: IconWaste,
    INVENTORY_SKU: IconSKU,
    INVENTORY_RECIPES: IconRecipes,

    // STAFFING
    STAFFING_EMPLOYEE_DIRECTORY: IconEmpDir,
    STAFFING_EMPLOYEE_ATTENDANCE: IconEmpAttn,
    STAFFING_SHIFT_PLANNING: IconPlan,
    STAFFING_STAFF_SETTINGS: IconSettings,

    // CRM
    CRM_DASHBOARD: IconDashboard,
    CRM_CUSTOMER_SEGMENTS_PROFILES: IconCustomerSegment,
    CRM_MEMBERS_INFO: IconCustomerDashboard,
    CRM_CAMPAIGN_CONTROL: IconCampaignControl,
    CRM_LOYALTY_REWARDS: IconLoyalty,

    // FINANCE
    FINANCE_DASHBOARD: IconDashboard,
    FINANCE_PROFIT_ANALYTICS: IconProfitAnalytics,
    FINANCE_INVOICE_COGS_REPORT: IconInvoicereport,
    FINANCE_SALES_REPORT: IconSalesReport,
    FINANCE_TAXATION_COMPLIANCE: IconTaxation,
    FINANCE_TRANSACTIONS: IconTransactions,

    RESTAURANT_ADMIN_DASHBOARD: IconDashboard,
    RESTAURANT_ADMIN_POS: IconSupplyChain,
    RESTAURANT_ADMIN_FINANCE_ANALYSIS: IconFinanceAnalysis,
    RESTAURANT_ADMIN_SETTINGS: IconSettings,
    RESTAURANT_ADMIN_FX_SUPPORT: IconFXSupport,
    RESTAURANT_ADMIN_INTEGRATION: IconIntegration,
    RESTAURANT_ADMIN_MANAGE_NEWS_FEED: IconNewsFeed,
};

const getMenuIcon = (menu: MenuItem) =>
    (menu.code && MENU_ICON_MAP[menu.code]) || IconSupplyChain;

/* ---------------- HELPERS ---------------- */
const removeMobileMenuClass = () => {
    document.body.classList.remove("show-mo-menu");
};

const getChildren = (menu: MenuItem): MenuItem[] =>
    (menu.subMenus || menu.sub_menus || []).filter((m) => m.is_enabled !== false && m.is_menu !== false);

/* ---------------- COMPONENT ---------------- */
const VerticalMenu = () => {
    const location = useLocation();
    const { selectedMenuKey } = useMenu();
    const { runTour } = useOnboarding();
    const [openMenuCode, setOpenMenuCode] = useState<string | null>(null);
    const menuList = useSelector((state: any) => state.items?.menuList);
    const [menus, setMenus] = useState<MenuItem[]>([]);
    const [showLogout, setShowLogout] = useState(false);
    const [collapsed, setCollapsed] = useState(true);
    const [menuOpen, setMenuOpen] = useState(false);
    const isSidebarOpen = menuOpen || runTour;


    const [showAiChat, setShowAiChat] = useState(false);
    const handleChatBtnClick = () => setShowAiChat(true);
    const handleCloseAiChat = () => setShowAiChat(false);

    const isActiveRoute = (path?: string) =>
        path ? location.pathname.startsWith(path) : false;

    const isAnyChildActive = (menu: MenuItem): boolean =>
        getChildren(menu).some(
            (child) =>
                isActiveRoute(buildUrl(child)) || isAnyChildActive(child)
        );
    useEffect(() => {
        document.body.classList.toggle("collapse-menu", !isSidebarOpen);
    }, [isSidebarOpen]);
    useEffect(() => {
        setMenus(menuList);
    }, [menuList]);
    useEffect(() => {
        if (openMenuCode) {
            const stillInside = menus.some(menu =>
                getChildren(menu).some(
                    child =>
                        child.code === openMenuCode &&
                        isAnyChildActive(child)
                )
            );

            if (!stillInside) {
                setOpenMenuCode(null);
            }
        }
    }, [location.pathname]);
    const toggleMenu = () => {
        setCollapsed((prev) => {
            document.body.classList.toggle("collapse-menu", !prev);
            return !prev;
        });
    };

    const handleLogout = () => {
        WebService.addLoader("deleteBtnModal");
        WebService.logout();
        WebService.removeLoader("deleteBtnModal");
        setShowLogout(false);
        removeFcmToken();
    };

    const removeFcmToken = () => {
        let body = {
            token: localStorage.getItem('fcm_token')
        }
        WebService.deleteAPI({
            action: 'staffs/remove-fcm-token',
            body: body,
            isShowError: true,
            btnId: 'deleteBtnModal'
        }).then((response) => {
            localStorage.removeItem('fcm_token');
        });
    };

    /* -------- MODULE FILTER -------- */
    const activeMenus = menus?.filter((menu) => {
        if (menu.is_enabled === false) return false;
        if (selectedMenuKey === MenuKeys.POS) return menu.code === "POS";
        if (selectedMenuKey === MenuKeys.INVENTORY)
            return menu.code === "INVENTORY";
        if (selectedMenuKey === MenuKeys.STAFFING) return menu.code === "STAFFING";
        if (selectedMenuKey === MenuKeys.CRM) return menu.code === "CRM";
        if (selectedMenuKey === MenuKeys.FINANCE) return menu.code === "FINANCE";
        if (selectedMenuKey === MenuKeys.RESTAURANT_ADMIN) {
            return menu.code === "RESTAURANT_ADMIN";
        } else {
            return true;
        }

        return false;
    });

    /* -------- URL BUILDER -------- */
    const buildUrl = (menu: MenuItem) => {
        if (!menu.url) return "#";
        if (menu.code?.startsWith("POS_")) return `/pos${menu.url}`;
        if (menu.code?.startsWith("INVENTORY_")) return `/inventory${menu.url}`;
        if (menu.code === 'SUPPLY_CHAIN_DASHBOARD') return `/inventory${menu.url}`;
        if (menu.code?.startsWith("CRM_")) return `/crm${menu.url}`;
        if (menu.code?.startsWith("STAFFING_")) return `/staffing${menu.url}`;
        if (menu.code?.startsWith("FINANCE_")) return `/finance${menu.url}`;
        if (menu.code?.startsWith("RESTAURANT_ADMIN_"))
            return `/restaurant-admin${menu.url}`;
        return menu.url;
    };
    const renderMenuItem = (menu: MenuItem, isChild = false) => {
        if (menu.is_enabled === false) return null;
        const children = getChildren(menu);
        const hasChildren = children.length > 0;
        const isChildActive = hasChildren ? isAnyChildActive(menu) : false;
        const isOpen = openMenuCode === menu.code || runTour || isChildActive;
        if (!hasChildren) {
            return (
                <Link
                    key={menu.id || menu.menu_id}
                    to={buildUrl(menu)}
                    className={clsx(
                        "menu-item",
                        `tour-${menu.code}`,
                        isActiveRoute(buildUrl(menu)) && "active"
                    )}
                    onClick={removeMobileMenuClass}
                >
                    {!isChild && (
                        <img src={getMenuIcon(menu)} className="menu-icon" />
                    )}
                    <span className="nav-text">{menu.title}</span>
                </Link>
            );
        }
        return (
            <div key={menu.id || menu.menu_id}>
                <div
                    className={clsx(
                        "menu-item",
                        `tour-${menu.code}`,
                        (isOpen || isChildActive) && "active"
                    )}
                    onClick={() =>
                        setOpenMenuCode(prev =>
                            prev === menu.code ? null : menu.code!
                        )
                    }
                >
                    {!isChild && (
                        <img src={getMenuIcon(menu)} className="menu-icon" />
                    )}
                    <span className="nav-text">{menu.title}</span>
                    <span className="down-arrow">
                        <IoChevronDownOutline />
                    </span>
                </div>

                {isSidebarOpen && (
                    // <Collapse in={isOpen || isChildActive}>
                    <Collapse in={isOpen}>
                        <div className="sub-menu">
                            {children.map(child => renderMenuItem(child, true))}
                        </div>
                    </Collapse>
                )}
            </div>
        );
    };

    return (
        <>
            {/* <div
                id="vertical_menu"
                className={`verticle-menu ${collapsed ? "collapsed-menu" : ""}`}
                onMouseEnter={toggleMenu}
                onMouseLeave={toggleMenu}
            > */}
            <div
                id="vertical_menu"
                className={`verticle-menu ${!isSidebarOpen ? "collapsed-menu" : ""}`}
                onMouseEnter={() => setMenuOpen(true)}
                onMouseLeave={() => setMenuOpen(false)}
            >
                <div className="menu-list">
                    {activeMenus?.flatMap((menu: any) =>
                        getChildren(menu).map((menu: any) => renderMenuItem(menu, false))
                    )}
                </div>

                {/* Footer */}
                {/* <div className="menu-footer">
                    <Link to="/profile" className="profile-link">
                        <svg
                            className="status-indicator position-absolute"
                            width="9"
                            height="9"
                        >
                            <circle cx="4.5" cy="4.5" r="4.5" fill="#67AF7A" />
                        </svg>
                        <Image
                            src={ProfileAvtar}
                            roundedCircle
                            width={40}
                            height={40}
                            className="rounded-circle object-fit-cover border mx-auto"
                        />
                    </Link>

                    <Button
                        className="mt-1 toggle-menu-btn"
                        variant="outline-danger2"
                        onClick={() => setShowLogout(true)}
                        style={{ width: 45, height: 45 }}
                    >
                        <RiLogoutBoxRLine size={20} />
                    </Button>
                </div> */}
            </div>

            {showAiChat ? <AiChat onClose={handleCloseAiChat} /> :
                <div className='chat-btn' onClick={handleChatBtnClick} style={{ cursor: 'pointer' }}>
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clip-path="url(#clip0_6477_5121)">
                            <mask id="mask0_6477_5121" style={{ maskType: "luminance" }} maskUnits="userSpaceOnUse" x="0" y="0" width="40" height="40">
                                <path d="M40 0H0V40H40V0Z" fill="white" />
                            </mask>
                            <g mask="url(#mask0_6477_5121)">
                                <path d="M4.48986 27.195C7.00374 26.6812 9.31122 25.44 11.1256 23.6257C12.9399 21.8113 14.1811 19.5039 14.6949 16.99L12.2457 16.4891C11.8301 18.5231 10.8259 20.3901 9.35791 21.858C7.88996 23.326 6.02299 24.3302 3.98902 24.7458L4.48986 27.195ZM4.48986 26.1383C4.60994 26.1664 4.71733 26.2335 4.79527 26.3291C4.87321 26.4247 4.91731 26.5434 4.92069 26.6666C4.91755 26.7901 4.87343 26.909 4.79528 27.0047C4.71713 27.1003 4.61023 27.1673 4.48986 27.195L3.98902 24.7458C1.89819 25.1733 1.89819 28.16 3.98902 28.5875L4.48986 26.1383ZM14.6949 36.3433C14.1811 33.8294 12.9399 31.5219 11.1256 29.7076C9.31122 27.8933 7.00374 26.6521 4.48986 26.1383L3.98902 28.5875C6.02299 29.0031 7.88996 30.0073 9.35791 31.4753C10.8259 32.9432 11.8301 34.8102 12.2457 36.8441L14.6949 36.3433ZM13.6382 36.3433C13.6663 36.2232 13.7334 36.1158 13.829 36.0379C13.9245 35.96 14.0432 35.9159 14.1665 35.9125C14.29 35.9156 14.4089 35.9597 14.5046 36.0379C14.6002 36.116 14.668 36.2238 14.6957 36.3441L12.2457 36.8441C12.6732 38.935 15.6599 38.935 16.0874 36.8441L13.6382 36.3433ZM23.8432 26.1383C21.3293 26.6521 19.0218 27.8933 17.2075 29.7076C15.3932 31.5219 14.152 33.8294 13.6382 36.3433L16.0874 36.8441C16.503 34.8102 17.5072 32.9432 18.9751 31.4753C20.4431 30.0073 22.3101 29.0031 24.344 28.5875L23.8432 26.1383ZM23.8432 27.195C23.7231 27.1669 23.6157 27.0998 23.5378 27.0042C23.4598 26.9086 23.4157 26.7899 23.4124 26.6666C23.4158 26.5433 23.46 26.4245 23.5381 26.3289C23.6162 26.2333 23.7238 26.1654 23.844 26.1375L24.344 28.5875C26.4349 28.16 26.4349 25.1733 24.344 24.7458L23.8432 27.195ZM13.6382 16.99C14.152 19.5039 15.3932 21.8113 17.2075 23.6257C19.0218 25.44 21.3293 26.6812 23.8432 27.195L24.344 24.7458C22.3101 24.3302 20.4431 23.326 18.9751 21.858C17.5072 20.3901 16.503 18.5231 16.0874 16.4891L13.6382 16.99ZM16.0874 16.4891C15.6599 14.3983 12.6732 14.3983 12.2457 16.4891L14.6957 16.9891C14.6677 17.1095 14.6006 17.2172 14.5048 17.2953C14.409 17.3734 14.2901 17.4176 14.1665 17.4208C14.0431 17.4174 13.9243 17.3732 13.8288 17.2951C13.7332 17.217 13.6661 17.1094 13.6382 16.9891L16.0874 16.4891Z" fill="white" />
                                <path d="M26.3249 17.8416C27.9303 17.5138 29.4039 16.7212 30.5625 15.5626C31.7212 14.404 32.5137 12.9304 32.8416 11.325L30.3924 10.825C30.1626 11.9502 29.6071 12.983 28.795 13.7951C27.983 14.6072 26.9501 15.1627 25.8249 15.3925L26.3249 17.8416ZM26.3249 15.4916C27.6041 15.7525 27.6041 17.5808 26.3249 17.8416L25.8249 15.3925C24.4382 15.6758 24.4382 17.6575 25.8249 17.9408L26.3249 15.4916ZM32.8416 22.0083C32.5137 20.4029 31.7212 18.9293 30.5625 17.7707C29.4039 16.6121 27.9303 15.8195 26.3249 15.4916L25.8249 17.9408C26.9501 18.1706 27.983 18.7261 28.795 19.5382C29.6071 20.3502 30.1626 21.3831 30.3924 22.5083L32.8416 22.0083ZM30.4916 22.0083C30.7524 20.7291 32.5807 20.7291 32.8416 22.0083L30.3924 22.5083C30.6757 23.895 32.6574 23.895 32.9407 22.5083L30.4916 22.0083ZM37.0082 15.4916C35.4028 15.8195 33.9292 16.6121 32.7706 17.7707C31.612 18.9293 30.8195 20.4029 30.4916 22.0083L32.9407 22.5083C33.1705 21.3831 33.726 20.3502 34.5381 19.5382C35.3502 18.7261 36.383 18.1706 37.5082 17.9408L37.0082 15.4916ZM37.0082 17.8416C35.7291 17.5808 35.7291 15.7525 37.0082 15.4916L37.5082 17.9408C38.8949 17.6575 38.8949 15.6758 37.5082 15.3925L37.0082 17.8416ZM30.4916 11.325C30.8195 12.9304 31.612 14.404 32.7706 15.5626C33.9292 16.7212 35.4028 17.5138 37.0082 17.8416L37.5082 15.3925C36.383 15.1627 35.3502 14.6072 34.5381 13.7951C33.726 12.983 33.1705 11.9502 32.9407 10.825L30.4916 11.325ZM32.9407 10.825C32.6574 9.43831 30.6757 9.43831 30.3924 10.825L32.8416 11.325C32.5799 12.6041 30.7524 12.6041 30.4916 11.325L32.9407 10.825ZM19.2191 2.15581C19.3924 1.30581 20.6074 1.30581 20.7816 2.15581C20.9692 3.07448 21.4228 3.91774 22.0858 4.58076C22.7488 5.24378 23.5921 5.69731 24.5107 5.88498C25.3607 6.05914 25.3607 7.27414 24.5107 7.44831C23.5921 7.63598 22.7488 8.08951 22.0858 8.75253C21.4228 9.41554 20.9692 10.2588 20.7816 11.1775C20.6074 12.0275 19.3924 12.0275 19.2191 11.1775C19.0313 10.2587 18.5777 9.41536 17.9145 8.75233C17.2513 8.0893 16.4079 7.63583 15.4891 7.44831C14.6391 7.27414 14.6391 6.05914 15.4891 5.88498C16.4079 5.69745 17.2513 5.24399 17.9145 4.58096C18.5777 3.91793 19.0313 3.0746 19.2191 2.15581Z" fill="#DAAF51" />
                            </g>
                        </g>
                        <defs>
                            <clipPath id="clip0_6477_5121">
                                <rect width="40" height="40" fill="white" />
                            </clipPath>
                        </defs>
                    </svg>
                </div>}
            <ConfirmationModal
                show={showLogout}
                setShow={setShowLogout}
                heading="Logout"
                acceptButtonText="LOGOUT"
                rejectButtonText="CANCEL"
                message="Are you sure you want to logout?"
                handleYesClick={handleLogout}
            />
        </>
    );
};

export default VerticalMenu;
